import BaseTibbit from '@appblocks/codegeneration/Tibbits/_BaseTibbit';
import Tibbits from '@appblocks/codegeneration/Tibbits/index';
import {
    CodeBlock, BlockType, SettingType, Setting, Define, TibboDataType,
} from '@appblocks/codegeneration/types';


const PIC_CLOCK_SPEED = [
    {
        // name: '32MHz (500Hz to 50kHz pulse frequencies)',
        name: '500Hz to 4kHz pulse frequencies',
        value: 'INTERNAL_OSCILLATOR_FREQ_32MHZ_HF',
    },
    {
        // name: '16MHz (250Hz to 50kHz pulse frequencies)',
        name: '250Hz to 50kHz pulse frequencies',
        value: 'INTERNAL_OSCILLATOR_FREQ_16MHZ_HF',
    },
    // {
    //     name: '8MHz',
    //     value: 'INTERNAL_OSCILLATOR_FREQ_8MHZ_HF',
    // },
    // {
    //     name: '4MHz',
    //     value: 'INTERNAL_OSCILLATOR_FREQ_4MHZ_HF',
    // },
    // {
    //     name: '2MHz',
    //     value: 'INTERNAL_OSCILLATOR_FREQ_2MHZ_HF',
    // },
    // {
    //     name: '1MHz',
    //     value: 'INTERNAL_OSCILLATOR_FREQ_1MHZ_HF',
    // },
];

export default class Tibbit_16 extends BaseTibbit {
    constructor() {
        super();
        this.id = 'tibbit_16';
        this.name = 'Tibbit 16';
        this.fileLinks = [
            // 'ssi\\ssi.tbs',
            // 'ssi\\ssi.tbh',
        ];
        this.files = [
            'libraries/super_i2c/super_i2c.tbh',
            'libraries/super_i2c/super_i2c.tbs',
            'libraries/pic/tbt_pic_gra.tbh',
            'libraries/pic/tbt_pic_gra.tbs',
            'libraries/ssi/ssi.tbh',
            'libraries/ssi/ssi.tbs',
        ];
        const settings: Array<Setting> = [
            {
                id: 'NAME',
                name: 'Name',
                settingType: SettingType.NAME,
                required: true,
                default: '',
            },
            {
                id: 'USESSI',
                name: 'Mode',
                settingType: SettingType.OPTION,
                readonly: true,
                required: true,
                default: 'NO',
                options: [
                    {
                        name: 'I2C mode',
                        value: 'YES',
                    },
                    {
                        name: 'Direct I/O (bit-banging)',
                        value: 'NO',
                    },
                ],
            },
            {
                id: 'PIC_CLOCK_SPEED',
                name: 'Clock Speed',
                settingType: SettingType.OPTION,
                readonly: true,
                required: true,
                default: 'INTERNAL_OSCILLATOR_FREQ_32MHZ_HF',
                options: PIC_CLOCK_SPEED,
            },
            {
                id: 'FREQUENCY',
                name: 'Frequency (Hz)',
                settingType: SettingType.NUMBER,
                required: true,
                min: 500,
                max: 4000,
                default: 500,
            },
            {
                id: 'L1NAME',
                name: 'Channel 1 Name',
                settingType: SettingType.NAME,
                dataType: TibboDataType.WORD,
                default: '',
                min: 0,
                max: 100,
                required: true,
            },
            {
                id: 'L2NAME',
                name: 'Channel 2 Name',
                settingType: SettingType.NAME,
                dataType: TibboDataType.WORD,
                default: '',
                min: 0,
                max: 100,
                required: true,
            },
            {
                id: 'L3NAME',
                name: 'Channel 3 Name',
                settingType: SettingType.NAME,
                dataType: TibboDataType.WORD,
                default: '',
                min: 0,
                max: 100,
                required: true,
            },
        ];

        this.settings = this.settings.concat(settings);
    }

    getDefines(): Array<Define> {
        return [
            {
                name: 'TBT_CLK_SOURCE',
                value: this.getSettingValue('PIC_CLOCK_SPEED'),
            },
            {
                name: 'PIC_DEBUG_PRINT',
                value: '1',
            },
        ];
    }

    getCodeBlocks() {
        const blocks: Array<CodeBlock> = [];

        if (this.project.runtime === 'simulator') {
            return [];
        }

        const socket = Tibbits.getSocket(this.getSettingValue('SOCKET'), this.project.device.id);
        const NAME = this.getSettingValue('NAME');
        const USESSI = this.getSettingValue('USESSI');
        const FREQUENCY = this.getSettingValue('FREQUENCY');
        blocks.push({
            location: 'boot',
            blockType: BlockType.CODE,
            template: `
    '${this.id} setup on socket ${this.getSettingValue('SOCKET')}
    '================================================================
    dim ${NAME}_pic_fw_str as string
    ${NAME}.PIC_CLK_PIN=${socket?.pins[0]}
    ${NAME}.PIC_DATA_PIN=${socket?.pins[1]}
    ${NAME}.PIC_RST_PIN=${socket?.pins[2]}
    ${NAME}.PIC_INT_PIN=${socket?.pins[3]}
    ${NAME}.TBT_CLK_SOURCE=${this.getSettingValue('PIC_CLOCK_SPEED')}
    select case tbt_pic_gra_init("${NAME}", ${NAME},${USESSI},${NAME}_pic_fw_str)
    case PIC_NG:
        sys.debugprint("SSI channel could not be allocated, using bit banging instead.\\r\\n")

    case PIC_REJ:
        sys.debugprint("The CPU clock is too slow for the SSI to be used. Bit banging used instead.\\r\\n")

    case PIC_FWNG:
        if ${NAME}_pic_fw_str="" then ${NAME}_pic_fw_str="<No version string returned>"
        sys.debugprint("PIC firmware version mismatch or PIC not present.\\r\\n")
        sys.debugprint("PIC reported this firmware version: \\r\\n"+${NAME}_pic_fw_str)
        sys.debugprint("This application expects the following firmware: "+EXPECTED_PIC_FIRMWARE_V+".XX\\r\\n")
        'sys.halt
    end select
    tbt_pic_gra_config(${NAME},PIC_PIN0,PIC_FUNC_PWM)
    tbt_pic_gra_config(${NAME},PIC_PIN1,PIC_FUNC_PWM)
    tbt_pic_gra_config(${NAME},PIC_PIN2,PIC_FUNC_PWM)
    '-----------------------------------------------------------------`,
        });

        blocks.push({
            location: 'GLOBAL_DECLARATIONS',
            blockType: BlockType.CODE,
            template: `
declare ${NAME} as tbt_pic_gra`,
        });

        blocks.push({
            location: 'GLOBAL_DIMS',
            blockType: BlockType.CODE,
            template: `
dim ${NAME} as tbt_pic_gra`,
        });

        for (let i = 0; i < 3; i++) {
            const DUTY_CYCLE_VAR = this.getSettingValue(`L${i + 1}NAME`);

            blocks.push({
                location: `var_${DUTY_CYCLE_VAR}_set`,
                blockType: BlockType.CODE,
                template: `
    tbt_pic_gra_pwm_config_simple(${NAME}, PIC_PWM${i}, ${FREQUENCY}, ${DUTY_CYCLE_VAR})
    `,
            });
        }


        return blocks;
    }
}
